import React, { useState, useEffect } from 'react';
import { X, Check, Crown, Sparkles, BarChart3, Target, Loader2 } from 'lucide-react';

const UpgradeModal = ({ isOpen, onClose, subscription }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('annual'); // Default to annual (best value)
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPricing();
    }
  }, [isOpen]);

  const fetchPricing = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/config`
      );
      if (response.ok) {
        const data = await response.json();
        setPricing(data.pricing);
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  const handleUpgrade = async (interval) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/create-checkout?interval=${interval}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      setError('Failed to start upgrade process. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const premiumFeatures = [
    {
      icon: Sparkles,
      title: 'Advanced AI Insights',
      description: 'Monthly, Quarterly, and Yearly insights with deep pattern analysis',
    },
    {
      icon: Target,
      title: 'Action Plan Management',
      description: 'Create and track structured action plans with tasks and progress',
    },
    {
      icon: BarChart3,
      title: 'Comprehensive Analytics',
      description: 'Advanced sentiment trends and emotional tracking over time',
    },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-brand-accent text-white p-6 rounded-t-2xl">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Crown className="h-8 w-8" />
                <h2 className="text-2xl font-bold">Upgrade to Premium</h2>
              </div>
              <p className="text-white/80">Choose the plan that works best for you</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Current Plan */}
          {subscription && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Current Plan</p>
              <p className="font-semibold text-gray-900 capitalize">
                {subscription.subscription_tier}
                {subscription.subscription_tier === 'trial' && subscription.days_remaining !== null && (
                  <span className="ml-2 text-sm font-normal text-orange-600">
                    ({subscription.days_remaining} day{subscription.days_remaining !== 1 ? 's' : ''} remaining)
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Plan Selection */}
          <div className="mb-8">
            <h3 className="font-semibold text-gray-900 mb-4 text-center">Select Your Plan:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Annual Plan - Recommended */}
              <button
                onClick={() => setSelectedPlan('annual')}
                className={`relative p-6 rounded-xl border-2 transition-all ${
                  selectedPlan === 'annual'
                    ? 'border-brand-accent bg-brand-accent/5'
                    : 'border-gray-200 hover:border-brand-border'
                }`}
              >
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-brand-ink px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                    BEST VALUE • SAVE {pricing?.annual?.savings_percentage || 17}%
                  </span>
                </div>
                <div className="text-center mt-2">
                  <p className="text-gray-600 text-sm mb-2">Annual Plan</p>
                  <div className="flex items-baseline justify-center space-x-2 mb-2">
                    <span className="text-4xl font-bold text-brand-accent">
                      {pricing?.annual?.formatted || '£10.00'}
                    </span>
                    <span className="text-gray-600">/year</span>
                  </div>
                  <p className="text-sm text-brand-accent font-semibold">
                    Only {pricing?.annual?.monthly_equivalent ? `£${pricing.annual.monthly_equivalent.toFixed(2)}` : '£0.83'}/month
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Billed annually</p>
                </div>
                {selectedPlan === 'annual' && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-brand-accent rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </button>

              {/* Monthly Plan */}
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`relative p-6 rounded-xl border-2 transition-all ${
                  selectedPlan === 'monthly'
                    ? 'border-brand-accent bg-brand-accent/5'
                    : 'border-gray-200 hover:border-brand-border'
                }`}
              >
                <div className="text-center">
                  <p className="text-gray-600 text-sm mb-2">Monthly Plan</p>
                  <div className="flex items-baseline justify-center space-x-2 mb-2">
                    <span className="text-4xl font-bold text-gray-900">
                      {pricing?.monthly?.formatted || '£1.00'}
                    </span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Billed monthly
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Cancel anytime</p>
                </div>
                {selectedPlan === 'monthly' && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-brand-accent rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Premium Features */}
          <div className="mb-8">
            <h3 className="font-semibold text-gray-900 mb-4">What&apos;s included in Premium:</h3>
            <div className="space-y-4">
              {premiumFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="bg-brand-accent/10 rounded-lg p-2 mt-0.5">
                      <Icon className="h-5 w-5 text-brand-accent" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{feature.title}</p>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                    <Check className="h-5 w-5 text-green-500 mt-1" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Basic Features Included */}
          <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-medium text-green-800 mb-2">Plus all Basic features:</p>
            <ul className="text-sm text-green-700 space-y-1">
              <li>✓ Unlimited reflection capture</li>
              <li>✓ Weekly AI summaries</li>
              <li>✓ Mood and sentiment tracking</li>
            </ul>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleUpgrade(selectedPlan)}
              disabled={loading}
              className="flex-1 bg-brand-cta hover:bg-brand-cta-hover text-white py-3 px-6 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Crown className="h-5 w-5" />
                  <span>
                    Upgrade {selectedPlan === 'annual' ? 'Annual' : 'Monthly'} - {
                      selectedPlan === 'annual' 
                        ? (pricing?.annual?.formatted || '£10/year')
                        : (pricing?.monthly?.formatted || '£1/month')
                    }
                  </span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>

          {/* Terms */}
          <p className="text-xs text-gray-500 text-center mt-4">
            Secure payment powered by Stripe. Cancel anytime from your account settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
